// @atlas/contracts — a `meetings` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { MEETING_LOCATION_KEYS } from '@atlas/shared';
import { z } from 'zod';
import { isoDateStr, nullableStr } from '../_shared/schemas.js';
/**
 * Meeting-sor (találkozók lista-nézet, belső): a `communication.meetings` [T] busz-erőforrás fehérlistás
 * mezői (title/description/locationKey/startsAt/endsAt/online-linkek) + a szervező (`createdBy` identity →
 * `organizerName` foundation-feloldás) és a résztvevő-szám (`communication.meeting-participants` a
 * `meetingId`-re aggregálva, a törölt sorok nélkül). Az `online` jelzi, hogy van-e BÁRMELY online link
 * (zoom/meet/teams). A `startsAt`/`endsAt` UTC ISO — a megjelenítés-formázás (Europe/Budapest) a UI dolga.
 * Nincs sor-részletnézet (v1 lista). A `legacyId` sosem jön.
 */
/**
 * Egy meeting-résztvevő (a lista-cella + a szerkesztő-sheet résztvevő-listája). Az `id` a résztvevő
 * identity-je (a foundation `identities`-ből feloldott `name` + `avatarUrl` monogram-fallbackkel), a
 * `participantRole` a résztvevő típusa (`internal` = munkatárs | `partner` = kapcsolattartó — a
 * `communication/meeting-participants` sor `participantRole`-ja), a `status` a legacy részvételi-szándék
 * enum (`pending` | `confirmed` | `declined` — a UI színezi/címkézi), a `respondedAt` az RSVP-válasz
 * időpontja (UTC ISO; null, amíg nem válaszolt).
 */
export const MeetingParticipantSchema = z.object({
    id: z.string(),
    /** `null`, ha az identity nem oldható fel (törölt személy) — a felület „Törölt felhasználó"-t mutat, nem UUID-t. */
    name: z.string().nullable(),
    /**
     * Strukturált név-részek a NYELV-TUDATOS névsorrendhez (magyarul vezetéknév elöl) — a `name` a
     * locale-független fallback (displayName). A sorrendet a KLIENS dönti el (`PersonChip`/`formatName`), mert a
     * nyelvet a felület váltja futásidőben; a szerver ugyanazt a választ szolgálja ki mindkét nyelven.
     */
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    /** Presigned avatar-URL (SeaweedFS, S3-kompatibilis objektum-tár) vagy null → a UI monogram-fallbackot rajzol. */
    avatarUrl: z.string().nullable(),
    /** A kolléga csapat-színe — az avatar háttere (partner-résztvevőnél null). */
    teamColor: z.string().nullable(),
    /** internal | partner — a résztvevő típusa (a munkatárs/kapcsolattartó szétválasztás forrása). */
    participantRole: z.string(),
    /** pending | confirmed | declined (a legacy MeetingParticipantStatusEnum 1:1). */
    status: z.string(),
    /** Az RSVP-válasz időpontja (UTC ISO) — null, amíg a résztvevő nem válaszolt (pending). */
    respondedAt: z.string().nullable(),
});
/**
 * ISMÉTLŐDÉSI SZABÁLY (sorozat) — a létrehozáskor megadott szabály, ill. a sor sorozat-adata. Az alkalmak
 * MATERIALIZÁLT meeting-sorok (`seriesId`), tehát minden alkalom a szokásos ütközés-, RSVP-, emlékeztető- és
 * értesítés-logikán megy. `frequency`: `weekly` (hetente, a `weekdays` napjain) · `biweekly` (kéthetente, a
 * `weekdays` napjain) · `monthly` (havonta, a hónap ugyanazon napján — ha a hónapban nincs ilyen nap, az alkalom
 * kimarad) · `workdays` (minden munkanapon, H–P). `weekdays`: ISO hét-napok (1 = hétfő … 7 = vasárnap) — heti/
 * kétheti szabálynál kötelező, egyébként üres. A sorozat vége a `untilDate` (Budapest-nap, inkluzív) VAGY az
 * `occurrenceCount` — pontosan az egyik. A tartalmi szabályokat (pontosan egy vég, alkalom-plafon) a BFF
 * kényszeríti 422 mező-hibával, hogy a felület a hibás mezőre mutasson.
 */
export const MeetingRecurrenceSchema = z.object({
    frequency: z.enum(['weekly', 'biweekly', 'monthly', 'workdays']),
    weekdays: z.array(z.number().int().min(1).max(7)).max(7).default([]),
    untilDate: isoDateStr.nullable().optional(),
    occurrenceCount: z.number().int().positive().nullable().optional(),
});
/**
 * A sor sorozat-adata (olvasás) — a szabály a megjelenítéshez („hetente kedden · 12 alkalom"). A szabály a
 * létrehozáskori állapot (utólag nem módosul); a `liveCount` / `lastDate` a sorozat MA meglévő (nem törölt)
 * alkalmaiból számolt élő adat — darabszám és az utolsó alkalom Budapest-napja. Az élő olvasás hibájánál `null`,
 * ilyenkor a felület a szabályt mutatja.
 */
export const MeetingSeriesInfoSchema = z.object({
    id: z.string(),
    frequency: z.string(),
    weekdays: z.array(z.number()),
    untilDate: z.string().nullable(),
    occurrenceCount: z.number().nullable(),
    liveCount: z.number().nullable(),
    lastDate: z.string().nullable(),
});
/**
 * Sorozat-alkalom szerkesztésének / törlésének HATÓKÖRE: `this` (csak ez az alkalom — az alapértelmezés, és az
 * egyetlen értelmes érték egyszeri megbeszélésnél) · `following` (ez és a sorozat későbbi alkalmai) · `all` (a
 * sorozat összes, MÉG NEM ELKEZDŐDÖTT alkalma — a múltbeli alkalom változatlan marad, az a történet).
 */
export const MeetingSeriesScopeQuerySchema = z.object({
    scope: z.enum(['this', 'following', 'all']).optional(),
});
export const MeetingRowSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    /** A helyszín-kulcs (a közös `MEETING_LOCATION_KEYS` egyike — régi adaton más is lehet). */
    locationKey: z.string().nullable(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    /** Van-e bármely online link (zoom / google meet / ms teams). A LISTA csak ezt a flaget jeleníti. */
    online: z.boolean(),
    /** A konkrét online-linkek + Zoom-fiók — a LISTÁBAN nem jelennek meg, de a SZERKESZTŐ-form előtöltéséhez kellenek. */
    zoomAccountKey: z.string().nullable(),
    zoomLink: z.string().nullable(),
    googleMeetLink: z.string().nullable(),
    microsoftTeamsLink: z.string().nullable(),
    organizerId: z.string().nullable(),
    organizerName: z.string().nullable(),
    /** A szervező név-részei (ugyanaz az ok, mint a résztvevőknél): a megjelenítési sorrend kliens-oldali. */
    organizerGivenName: z.string().nullable(),
    organizerFamilyName: z.string().nullable(),
    /** A meetinghez tartozó (nem-törölt) résztvevők — feloldott név + RSVP-státusz (a lista-cellához). */
    participants: z.array(MeetingParticipantSchema),
    /** A meetinghez tartozó (nem-törölt) résztvevők száma (= participants.length; meta/darabszám). */
    participantCount: z.number(),
    /** OCC-verzió (a busz-sor `version`-je) — a szerkesztés `If-Match`-éhez. */
    version: z.number(),
    /**
     * Az ismétlődő sorozat azonosítója — `null` az egyszeri megbeszélésnél. EZ dönti el, hogy sorozat-alkalom-e (a
     * hatókör-választás forrása); a `series` szabály csak megjelenítés, és egy átmeneti olvasási hibánál `null` lehet.
     */
    seriesId: z.string().nullable(),
    /** Az ismétlődő sorozat szabálya (megjelenítéshez) — `null` az egyszerinél VAGY ha átmenetileg nem olvasható. */
    series: MeetingSeriesInfoSchema.nullable(),
});
/**
 * Meeting create/update közös body — a `communication/meetings` [T] busz-sor whitelistelt, szerkeszthető mezői
 * (a találkozó-entitás: cím/helyszín/időpont/online-linkek) + a RÉSZTVEVŐ-halmaz (`employeeIds` = belső
 * munkatársak, `contactIds` = partner kapcsolattartók). A résztvevőket a BFF pivot-diffeli a
 * `communication/meeting-participants` pivotra (kívánt halmaz vs meglévő → hiányzó create `pending`
 * státusszal, törlendő soft-delete; a meglévő sorok RSVP-státusza megőrződik). A meghívó/RSVP-értesítő
 * e-mail KÜLÖN (e-mail-fázis). Az üzleti invariánsokat (end>start, nem-múltbeli, online-link-exkluzivitás) a
 * BFF kényszeríti (422 mező-hibával). Az `startsAt`/`endsAt` ISO datetime (UTC). Az `undefined` mező érintetlen.
 */
export const MeetingWriteBodySchema = z.object({
    title: z.string().trim().min(1).max(255),
    description: nullableStr(255),
    locationKey: z.string().trim().min(1).max(64),
    startsAt: z.string().min(1),
    endsAt: z.string().min(1),
    zoomAccountKey: nullableStr(64),
    zoomLink: nullableStr(2083),
    googleMeetLink: nullableStr(2083),
    microsoftTeamsLink: nullableStr(2083),
    /** A KÍVÁNT belső résztvevők (identity type=internal); a szerver diffeli a meglévő résztvevő-sorokkal. */
    employeeIds: z.array(z.string()).default([]),
    /** A KÍVÁNT kapcsolattartó-résztvevők (identity type=partner). */
    contactIds: z.array(z.string()).default([]),
    /**
     * ISMÉTLŐDÉS — CSAK létrehozáskor: a szabály szerint a BFF kibontja az alkalmakat, és a sorozatot a busz atomi
     * kötegével írja (sorozat + alkalmak + résztvevők egy tranzakcióban). Szerkesztésnél nem módosítható (422).
     */
    recurrence: MeetingRecurrenceSchema.nullable().optional(),
});
/**
 * Saját RSVP-válasz body — a bejelentkezett résztvevő a SAJÁT részvételi-szándékát állítja. A `pending`
 * (kezdő) állapotra visszaállni nem lehet (csak `confirmed` / `declined`); a szerver a `respondedAt`-ot a
 * jelen időpontra állítja. A cél-sort a token `sub`-ja azonosítja (self-scope, IDOR-mentes — nincs identity-id
 * a path-ban).
 */
export const MeetingRsvpBodySchema = z.object({
    status: z.enum(['confirmed', 'declined']),
});
/**
 * Meeting-lista lekérdezés: szabad-szöveg (cím) + opcionális dátum-tartomány a `startsAt`-ra (from/to,
 * ISO date vagy datetime) + offset-lapozás (a BFF-szűrt halmazon). A rendezés fixen `startsAt` DESC.
 */
export const MeetingListQuerySchema = z.object({
    q: z.string().optional(),
    /** Kezdő-időpont alsó határa (inkluzív, ISO) — a legacy naptár-tartomány szűrője. */
    from: z.string().optional(),
    /** Kezdő-időpont felső határa (inkluzív, ISO). */
    to: z.string().optional(),
    /** A fix `startsAt` rendezés iránya (alap: `desc` — a legacy naptár-lista legfrissebb-elöl). */
    order: z.enum(['asc', 'desc']).optional(),
    /**
     * HELYSZÍN-szűrő (MULTI): ismételt query-param VAGY egyetlen érték; üres / hiányzó → minden helyszín. A kulcsok a
     * közös `MEETING_LOCATION_KEYS`-ből — ismeretlen kulcs → 400 (a query-validáció; nem néma üres lista).
     */
    location: z
        .preprocess((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]), z.array(z.enum(MEETING_LOCATION_KEYS)))
        .optional(),
    /**
     * KOLLÉGA-szűrő (MULTI, belső identity-id-k): csak azok a megbeszélések, ahol a kiválasztottak BÁRMELYIKE
     * RÉSZTVEVŐ, és a részvétele nem lemondott (`confirmed` vagy még `pending` — utóbbinál nem tudjuk, hogy szabad-e).
     * A SZERVEZŐ nem számít (a szervező magát ki is veheti a résztvevők közül). Üres / hiányzó → nincs szűrés.
     */
    participant: z
        .preprocess((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]), 
    // csak memóriában hasonlítjuk (nem kerül upstream-útvonalba), ezért elég a hossz-korlát
    z.array(z.string().min(1).max(64)).max(50))
        .optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/**
 * RÉSZTVEVŐ-ÜTKÖZÉS ellenőrzése MENTÉS ELŐTT (figyelmeztetés, NEM tiltás — user-döntés): a megadott belső
 * munkatársak közül ki szerepel MÁR egy másik, átfedő idősávú megbeszélésen. A felület ebből sárga, megerősítéssel
 * átléphető figyelmeztetést ad (tudatosan két helyre beírt kolléga) — a mentést a szerver NEM tiltja.
 *
 * `employeeIds`: query-stringben ismételt paraméter; egyetlen érték is tömbre normalizálódik. `excludeId`: a
 * szerkesztett megbeszélés saját azonosítója (önmagával nem ütközik).
 */
export const MeetingParticipantConflictQuerySchema = z.object({
    startsAt: z.string().min(1),
    endsAt: z.string().min(1),
    employeeIds: z.preprocess((v) => (v === undefined ? [] : Array.isArray(v) ? v : [v]), z.array(z.string()).max(200)),
    excludeId: z.string().optional(),
    /**
     * Sorozat létrehozásakor a szabály (lapos query-alakban): a BFF ugyanazzal a kibontással MINDEN alkalomra
     * ellenőriz. Hiányzó `frequency` → egyetlen idősáv.
     */
    frequency: z.enum(['weekly', 'biweekly', 'monthly', 'workdays']).optional(),
    weekdays: z.preprocess((v) => (v === undefined ? [] : Array.isArray(v) ? v : [v]), z.array(z.coerce.number().int().min(1).max(7)).max(7)),
    untilDate: isoDateStr.optional(),
    occurrenceCount: z.coerce.number().int().positive().optional(),
});
/**
 * Egy szervezeti egység a résztvevő-választó csoport-fájában — a név a lokalizált felirat (hu elöl, en
 * tartalék), mint az üzenet-címzett választó csapat/részleg opcióinál.
 */
const MeetingEmployeeTeamSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** a csapat FELVEHETŐ munkatársai (a gépi fiókok nélkül) — csak az `employees` listában szereplő id-k */
    memberIds: z.array(z.string()),
});
/**
 * A munkatárs-választó CSOPORT-fája: részleg → csapatai → tagok. Csak a VÁLASZTÁST segíti (egy részleg vagy csapat
 * egy kattintással kijelölhető) — a mentés ugyanúgy egyedi munkatárs-id-ket kap, csoportot nem tárolunk. A részleg
 * `memberIds`-e a csapatai tagjainak uniója; részleg nélküli csapat önálló csoportként, `teams: []`-mel jön.
 */
export const MeetingEmployeeGroupSchema = MeetingEmployeeTeamSchema.extend({
    teams: z.array(MeetingEmployeeTeamSchema),
});
/**
 * Egy ütközés: a munkatárs (név-részekkel, a nyelv-tudatos névsorrendhez) és az a MÁSIK megbeszélés, amelyen az
 * átfedő idősávban már szerepel (cím + UTC idősáv).
 */
export const MeetingParticipantConflictSchema = z.object({
    identityId: z.string(),
    /** `null`, ha az identity nem oldható fel (törölt személy). */
    name: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    meetingId: z.string(),
    meetingTitle: z.string(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
});
//# sourceMappingURL=schemas.js.map